import { WebVTT } from 'videojs-vtt.js';
import { getMimeFromExtension } from './mime-types';
import fileExtension from '../../utils/file-extension';
import isString from './is-string';
import isObject from './is-object';
import isArray from './is-array';
import mimeExtension from './mime-extension';
import logger from '../../utils/logger';
import decode_html_entities from '../../utils/decode-html-entities';
import { METADATA_TYPE_PODCAST, METADATA_TYPE_STREAM, METADATA_TYPE_AD_CLIENT, METADATA_TYPE_AD_SERVER, METADATA_TYPE_PLAYLIST } from '../constants';

/**
 * Creates a metadata model
 *
 * @param {Player} player Player instance
 * @param {object} metadata
 *
 * @return {object}
 */
export function createMetadata (metadata) {
  const icons = (metadata && metadata.icons) || [];
  const banners = (metadata && metadata.banners) || [];
  const contentType = (metadata && metadata.contentType) || METADATA_TYPE_PODCAST;
  const duration = (metadata && metadata.duration) || 0;
  let publishDate = (metadata && metadata.publishDate) || null;

  if (publishDate && !(publishDate instanceof Date)) {
    publishDate = new Date(publishDate);
  }

  const meta = Object.assign({
    uid: null,
    url: '',
    title: '',
    creator: '',
    grouping: '',
    description: '',
    language: 'en'
  }, metadata, {
    contentType: contentType,
    duration: contentType === METADATA_TYPE_STREAM ? -1 : duration,
    icons: icons.map(createImage),
    banners: banners.map(createImage),
    publishDate: publishDate
  });

  meta.title = decode_html_entities(meta.title);
  meta.creator = decode_html_entities(meta.creator);
  meta.grouping = decode_html_entities(meta.grouping);
  meta.description = decode_html_entities(meta.description);

  return meta;
}

/**
 * Creates an image model
 *
 * @param {object} img
 *
 * @return {object}
 */
export function createImage (img) {
  return {
    size: img.size,
    url: img.url
  };
}

/**
 * Creates an advertisement model
 *
 * @param {Player} player Player instance
 * @param {object} ad
 *
 * @return {object}
 */
export function createAdvertisement (ad) {
  const metadata = Object.assign({
    contentType: METADATA_TYPE_AD_CLIENT
  }, ad.metadata || {});

  const advert = Object.assign({
    position: 0,
    metadata: {},
    audio: [],
    companions: [],
    links: [],
    tags: []
  }, ad, {
    metadata: createMetadata(metadata),
    played: false,
    interrupted: false,
    skipped: false,
    complete: false,
    loaded: false
  });

  if (metadata.contentType === METADATA_TYPE_AD_CLIENT && ad.audio) {
    advert.audio = ad.audio.map(createAudioSource);
  }

  // force position on pre and post rolls
  if (advert.slot === 'pre') {
    advert.position = 0;
  } else if (advert.slot === 'post') {
    advert.position = -1;
  }

  // insert missing slot names
  if (advert.position === 0) {
    advert.slot = 'pre';
  } else if (advert.position === -1) {
    advert.slot = 'post';
  } else {
    advert.slot = 'mid';
  }

  return advert;
}

/**
 * Create an analytics tag
 *
 * @param {object} tag
 *
 * @return {object}
 */
export function createTag (tag) {
  return {
    service: tag.service || 'all',
    name: tag.name,
    value: tag.value
  };
}

/**
 * Create a playlist item link
 *
 * @param {object} link
 *
 * @return {object}
 */
export function createLink (link) {
  switch (link.kind) {
    case 'tracking':
    case 'impression':
    case 'error':
      return link;
    default:
      return {
        title: link.title,
        kind: link.kind || link.type,
        url: link.url,
        mime: link.mime,
        size: link.size
      };
  }
}

/**
 * Creates a playlist model
 *
 * @param {Player} player
 * @param {array} playlist
 *
 * @return {Object}
 */
export function createPlaylist (source) {
  const playlist = {
    metadata: {
      uid: null,
      title: '',
      contentType: METADATA_TYPE_PLAYLIST,
      url: '',
      icons: []
    },
    links: [],
    items: []
  };

  // load playlist array
  if (isArray(source)) {
    playlist.items = source;
  }
  // load playlist object
  else if (isObject(source) && source.metadata && source.metadata.contentType === METADATA_TYPE_PLAYLIST) {
    Object.assign(playlist.metadata, source.metadata);
    playlist.items = source.items;
  }
  // Load an audio source if the source is an object with any of the audio source properties
  else if (isObject(source) && (source.url)) {
    playlist.items = [
      { audio: [source] }
    ];
  }
  // Load a playlist item if the source is an object with any of the playlist item properties
  else if (isObject(source) && source.audio) {
    playlist.items = [source];
  }
  // Load a direct url
  else if (isString(source)) {
    playlist.items = [
      {
        audio: [{ url: source }]
      }
    ];
  }

  // Map over the playlist and create complete playlist items from the entries
  playlist.items = playlist.items.map(function (item) {
    // item.metadata = Object.assign({
    //   language: playlist.metadata.language,
    //   creator: playlist.metadata.creator,
    //   grouping: playlist.metadata.title
    // }, item.metadata);

    return createPlaylistItem(item);
  });

  return playlist;
}

/**
 * Creates a playlist item model
 *
 * @param {Player} player
 * @param {object} playlistItem
 *
 * @return {object}
 */
export function createPlaylistItem (playlistItem) {
  if (isString(playlistItem)) {
    playlistItem = {
      audio: [createAudioSource(playlistItem)]
    };
  }

  const returnItem = Object.assign({
    metadata: {},
    ads: [],
    audio: [],
    text: [],
    tags: [],
    links: []
  }, playlistItem);

  returnItem.metadata = createMetadata(playlistItem.metadata);
  returnItem.ads = returnItem.ads.map(createAdvertisement);
  returnItem.audio = returnItem.audio.map(createAudioSource);
  returnItem.text = returnItem.text.map(createTextTrack);
  returnItem.tags = returnItem.tags.map(createTag);
  returnItem.links = returnItem.links.map(createLink);

  return returnItem;
}

/**
 * Creates an audio source model
 *
 * @param {Player} player
 * @param {object} audioSource
 *
 * @return {object}
 */
export function createAudioSource (audioSource) {
  if (isString(audioSource)) {
    audioSource = { url: audioSource };
  }

  const source = Object.assign({
    url: '',
    quality: false,
    bitrate: 0,
    filesize: 0,
    mime: false,
    format: false,
    download: ''
  }, audioSource);

  if (!source.url) {
    logger.warn('Invalid audio source, missing required url');
    source.url = '';
  }

  if (!source.format) {
    source.format = fileExtension(source.url) || mimeExtension(source.mime);
  }

  if (!source.mime) {
    source.mime = getMimeFromExtension(source.format);
  }

  return source;
}

/**
 * Creates a text track model from a native text track
 *
 * @param {TextTrack} textTrack
 *
 * @return {object}
 */
export function createTextTrack (textTrack) {
  // From: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/track#attr-kind
  //  * If omitted the default kind is `subtitles`.
  //  * If the attribute contains an invalid value, it will use `metadata`.
  let kind = textTrack.kind || 'subtitles';
  if (['subtitles', 'captions', 'chapters', 'metadata', 'descriptions'].indexOf(textTrack.kind) === -1) {
    kind = 'metadata';
  }

  const mode = textTrack.mode || (kind === 'metadata' ? 'hidden' : 'disabled');

  return {
    label: textTrack.label || '',
    kind: kind,
    mode: mode,
    language: textTrack.language || 'en',
    url: textTrack.src || textTrack.url || ''
  };
}

/**
 * Creates a text track cue model from a native cue object
 *
 * @param {Player} player
 * @param {TextTrackCue} textTrackCue
 *
 * @todo don't convert cue text field to dom tree in the model factory function, instead move to TextTrackCue
 *
 * @return {object}
 */
export function createTextTrackCue (textTrackCue) {
  return {
    startTime: textTrackCue.startTime,
    endTime: textTrackCue.endTime,
    text: WebVTT.convertCueToDOMTree(window, textTrackCue.text).innerHTML
  };
}

/**
 * Creates a quality level
 *
 * @param {Player} player
 * @param {object} level
 *
 * @return {object}
 */
export function createQualityLevel (level) {
  return {
    label: level.label || '',
    bitrate: level.bitrate || 0
  };
}
